import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Globe, AlertTriangle, CheckCircle, RefreshCw, Settings,
  ChevronDown, ChevronRight, Loader, Shield, Info, TrendingUp, Zap,
  AlertCircle, Wrench, User, Clock, Mail, Lock, Radio, Monitor, Trash2, Cloud,
  ExternalLink
} from "lucide-react";
import Navigation from "../components/Navigation";
import Card from "../components/Card";
import Button from "../components/Buttons";
import { useApi } from "../hooks/useApi";
import { useRBAC } from "../hooks/useRBAC";

// ─── constants ───────────────────────────────────────────────────────────────

const SEVERITY_COLORS = {
  Critical: { badge: "bg-red-100 text-red-700 border border-red-200", bar: "bg-red-500" },
  High:     { badge: "bg-red-50 text-red-600 border border-red-200",  bar: "bg-red-400" },
  Medium:   { badge: "bg-orange-50 text-orange-600 border border-orange-200", bar: "bg-orange-500" },
  Low:      { badge: "bg-yellow-50 text-yellow-700 border border-yellow-200", bar: "bg-yellow-400" },
};

const RISK_RATING_STYLE = {
  CRITICAL: { pill: "bg-red-100 text-red-700 border border-red-200",        accent: "border-l-red-500" },
  HIGH:     { pill: "bg-orange-50 text-orange-700 border border-orange-200", accent: "border-l-orange-500" },
  MEDIUM:   { pill: "bg-yellow-50 text-yellow-700 border border-yellow-200", accent: "border-l-yellow-400" },
  LOW:      { pill: "bg-green-50 text-green-700 border border-green-200",    accent: "border-l-green-500" },
};

const RISK_STATUS_LABEL = {
  identified:  "Identified",
  in_progress: "In Progress",
  closed:      "Closed",
  accepted:    "Accepted",
};

const STATUS_OPTIONS = ["Open", "Acknowledged", "FalsePositive", "Resolved"];
const STATUS_LABEL   = { Open: "Open", Acknowledged: "Acknowledged", FalsePositive: "False Positive", Resolved: "Resolved" };

// When to use each action. Matches what the backend does: only Open findings count in the score;
// Resolved and False Positive close the linked risk; a rescan reopens a Resolved finding that is
// still there and auto-resolves an Acknowledged one that is gone, but never touches False Positive.
const STATUS_GUIDE = [
  { status: "Resolved",      label: "Mark Resolved",  when: "You have fixed the issue.",
    effect: "Closes the linked risk. If the next scan still finds the issue, it reopens automatically." },
  { status: "Acknowledged",  label: "Acknowledge",    when: "The issue is real and a fix is planned or under way.",
    effect: "Stops counting in the score while the linked risk stays open. If the next scan no longer finds it, it is resolved automatically." },
  { status: "FalsePositive", label: "False Positive", when: "The scanner is wrong: the issue does not exist or does not apply to you.",
    effect: "Closes the linked risk and stays False Positive even if later scans find it again. Give a reason so others can check it." },
  { status: "Open",          label: "Reopen",         when: "A finding was closed by mistake or needs work again.",
    effect: "Counts in the score again and reopens the linked risk." },
];
const GUIDE_BY_STATUS = Object.fromEntries(STATUS_GUIDE.map(g => [g.status, g]));

// IT Manager category metadata (maps to EsmFindingContextCatalog.RiskCategory on backend)
const CATEGORY_META = {
  EmailSecurity:    { label: "Email Security",    Icon: Mail,    color: "blue",   desc: "Your domain can be impersonated in phishing emails",       fixMin: 20 },
  CertificateHealth:{ label: "Certificate Health",Icon: Lock,    color: "yellow", desc: "Customers will see browser security warnings",              fixMin: 30 },
  ExposedServices:  { label: "Exposed Services",  Icon: Radio,   color: "red",    desc: "Critical systems are reachable directly from the internet", fixMin: 60 },
  WebSecurity:      { label: "Website Security",  Icon: Monitor, color: "orange", desc: "Your website has no protection against common web attacks",  fixMin: 45 },
  AssetHygiene:     { label: "Asset Hygiene",     Icon: Trash2,  color: "purple", desc: "Abandoned infrastructure could be hijacked by attackers",   fixMin: 10 },
  CloudPosture:     { label: "Cloud Posture",     Icon: Cloud,   color: "indigo", desc: "Azure cloud security misconfigurations detected by Microsoft Defender", fixMin: 90 },
};

const CAT_COLOR = {
  blue:   { pill: "bg-blue-50 text-blue-700 border border-blue-200",     active: "bg-blue-100 border-blue-400 text-blue-800" },
  yellow: { pill: "bg-yellow-50 text-yellow-700 border border-yellow-200", active: "bg-yellow-100 border-yellow-400 text-yellow-800" },
  red:    { pill: "bg-red-50 text-red-700 border border-red-200",         active: "bg-red-100 border-red-400 text-red-800" },
  orange: { pill: "bg-orange-50 text-orange-700 border border-orange-200", active: "bg-orange-100 border-orange-400 text-orange-800" },
  purple: { pill: "bg-purple-50 text-purple-700 border border-purple-200", active: "bg-purple-100 border-purple-400 text-purple-800" },
  indigo: { pill: "bg-indigo-50 text-indigo-700 border border-indigo-200", active: "bg-indigo-100 border-indigo-400 text-indigo-800" },
};

// ─── helpers ─────────────────────────────────────────────────────────────────

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const secs = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (secs < 60)   return "just now";
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  const days = Math.floor(secs / 86400);
  if (days === 1)  return "yesterday";
  if (days < 30)   return `${days} days ago`;
  if (days < 365)  return `${Math.floor(days / 30)} months ago`;
  return `${Math.floor(days / 365)}y ago`;
}

function isToday(dateStr) {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const n = new Date();
  return d.getDate() === n.getDate() && d.getMonth() === n.getMonth() && d.getFullYear() === n.getFullYear();
}

// ─── helpers ─────────────────────────────────────────────────────────────────

function formatImpact(val) {
  if (val == null) return '—';
  const n = Number(val);
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000)     return `$${(n / 1_000).toFixed(1)}K`;
  return `$${Math.round(n).toLocaleString()}`;
}

// ─── main component ───────────────────────────────────────────────────────────

export default function EsmDashboard() {
  const apiCall = useApi();
  const { canManageOrganizationRisks } = useRBAC();
  const org    = JSON.parse(localStorage.getItem("selectedOrganization") || "{}");
  const orgId  = org?.organizationId;
  const isAdmin = canManageOrganizationRisks(orgId);

  const [config,           setConfig]           = useState(null);
  const [summary,          setSummary]          = useState(null);
  const [findings,         setFindings]         = useState([]);
  const [allFindings,      setAllFindings]      = useState([]); // unfiltered, for asset counts
  const [risksByFindingId, setRisksByFindingId] = useState({});
  const [loading,          setLoading]          = useState(true);
  const [scanning,         setScanning]         = useState(false);

  const [filterSeverity, setFilterSeverity] = useState("");
  const [filterStatus,   setFilterStatus]   = useState("Open");
  const [filterType,     setFilterType]     = useState("");
  const [filterSource,   setFilterSource]   = useState("");
  const [filterCategory, setFilterCategory] = useState(""); // IT Manager category filter

  const buildQuery = useCallback(() => {
    const p = new URLSearchParams();
    if (filterSeverity) p.append("severity", filterSeverity);
    if (filterStatus)   p.append("status",   filterStatus);
    if (filterType)     p.append("findingType", filterType);
    return p.toString();
  }, [filterSeverity, filterStatus, filterType]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [cfg, sum, fnd, allFnd, rsk] = await Promise.all([
        apiCall("GET", "/esm/config").catch(() => null),
        apiCall("GET", "/esm/summary").catch(() => null),
        apiCall("GET", `/esm/findings?${buildQuery()}`).catch(() => []),
        apiCall("GET", "/esm/findings").catch(() => []),  // unfiltered for asset counts
        apiCall("GET", "/esm/risks").catch(() => []),
      ]);
      setConfig(cfg);
      setSummary(sum);
      setFindings(Array.isArray(fnd) ? fnd : []);
      setAllFindings(Array.isArray(allFnd) ? allFnd : []);
      const byId = {};
      if (Array.isArray(rsk)) rsk.forEach(r => { if (r.esmFindingId != null) byId[r.esmFindingId] = r; });
      setRisksByFindingId(byId);
    } finally {
      setLoading(false);
    }
  }, [buildQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadData(); }, [loadData]);

  async function handleTriggerScan() {
    setScanning(true);
    try {
      await apiCall("POST", "/esm/scan");
      setTimeout(() => { loadData(); setScanning(false); }, 70000);
    } catch { setScanning(false); }
  }

  async function handleStatusChange(findingId, newStatus, note) {
    try {
      await apiCall("PATCH", `/esm/findings/${findingId}/status`, { status: newStatus, note: note || null });
      setFindings(prev => prev.map(f => f.id === findingId ? { ...f, status: newStatus } : f));
      const [updatedSummary, updatedRisks, updatedFindings, updatedAllFindings] = await Promise.all([
        apiCall("GET", "/esm/summary").catch(() => null),
        apiCall("GET", "/esm/risks").catch(() => []),
        apiCall("GET", `/esm/findings?${buildQuery()}`).catch(() => null),
        apiCall("GET", "/esm/findings").catch(() => null),
      ]);
      if (updatedSummary) setSummary(updatedSummary);
      if (Array.isArray(updatedRisks)) {
        const byId = {};
        updatedRisks.forEach(r => { if (r.esmFindingId != null) byId[r.esmFindingId] = r; });
        setRisksByFindingId(byId);
      }
      if (Array.isArray(updatedFindings)) setFindings(updatedFindings);
      if (Array.isArray(updatedAllFindings)) setAllFindings(updatedAllFindings);
    } catch { alert("Failed to update status."); }
  }

  // IT Manager: compute category counts from risks
  const categoryBreakdown = Object.entries(CATEGORY_META).map(([key, meta]) => {
    const catRisks = Object.values(risksByFindingId).filter(r => r.category === key && r.status !== "deleted");
    const findingIds = new Set(catRisks.map(r => r.esmFindingId));
    const catFindings = allFindings.filter(f => findingIds.has(f.id));
    return {
      key,
      meta,
      count: catFindings.length,
      critical: catFindings.filter(f => f.severity === "Critical").length,
      high: catFindings.filter(f => f.severity === "High").length,
    };
  }).filter(c => c.count > 0);

  // Asset counts from all findings
  const uniqueDomains    = new Set(allFindings.map(f => f.domain)).size;
  const uniqueSubdomains = new Set(allFindings.map(f => f.subdomain).filter(Boolean)).size;

  // Apply category + source filters on top of current filtered findings
  const displayedFindings = findings.filter(f => {
    if (filterCategory) {
      const r = risksByFindingId[f.id];
      if (r?.category !== filterCategory) return false;
    }
    if (filterSource) {
      const src = getSource(f.evidence);
      if (filterSource === "Other") { if (src !== null) return false; }
      else if (src !== filterSource) return false;
    }
    return true;
  });

  const findingTypes = [...new Set(findings.map(f => f.findingType))].sort();

  if (loading) {
    return (
      <div className="min-h-screen bg-theme-bg">
        <Navigation />
        <div className="flex items-center justify-center py-24">
          <Loader className="animate-spin h-8 w-8 text-blue-500" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-theme-bg">
      <Navigation />
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">

        {/* ── Header ─────────────────────────────────────────────── */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Globe className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-content-primary">Surface Scan</h1>
              <p className="text-sm text-content-secondary">
                External attack surface — certs, DNS, headers, and open ports across your domains
              </p>
            </div>
          </div>
          {isAdmin && (
            <Button onClick={handleTriggerScan} disabled={scanning || !config} className="flex items-center gap-2">
              {scanning ? <><Loader className="animate-spin h-4 w-4" /> Scanning…</> : <><RefreshCw className="h-4 w-4" /> Scan Now</>}
            </Button>
          )}
        </div>

        {/* ── No config ──────────────────────────────────────────── */}
        {!config && (
          <Card className="mb-6 text-center py-12">
            <Globe className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-lg font-medium text-content-primary mb-2">No domains configured</h3>
            <p className="text-sm text-content-secondary mb-4">
              Add your organisation's domains to start monitoring your external attack surface.
            </p>
            {isAdmin && (
              <a href="/ConnectorsConfig" className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-purple-700 text-white text-sm font-medium hover:bg-purple-600">
                <Settings className="h-4 w-4" /> Configure Domains
              </a>
            )}
          </Card>
        )}

        {/* ── CIO: Attack Surface Score Card ─────────────────────── */}
        {summary && (
          <AttackSurfaceScoreCard
            summary={summary}
            domainCount={config?.domains?.length ?? uniqueDomains}
            subdomainCount={uniqueSubdomains}
          />
        )}

        {/* ── IT Manager: Category Breakdown ─────────────────────── */}
        {categoryBreakdown.length > 0 && (
          <div className="mb-6">
            <p className="text-xs font-semibold text-content-secondary uppercase tracking-wider mb-3">Risk Categories</p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {Object.entries(CATEGORY_META).map(([key, meta]) => {
                const cat = categoryBreakdown.find(c => c.key === key);
                if (!cat) return null;
                const { Icon, color, desc } = meta;
                const colors = CAT_COLOR[color];
                const isActive = filterCategory === key;
                return (
                  <button
                    key={key}
                    onClick={() => setFilterCategory(isActive ? "" : key)}
                    className={`text-left p-3 rounded-lg border transition-all ${isActive ? colors.active : colors.pill + " hover:opacity-80"}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <Icon className="h-4 w-4" />
                      <span className="text-lg font-bold">{cat.count}</span>
                    </div>
                    <div className="text-xs font-semibold leading-tight">{meta.label}</div>
                    <div className="text-[10px] mt-0.5 opacity-70 leading-tight">{desc}</div>
                    {(cat.critical > 0 || cat.high > 0) && (
                      <div className="flex gap-1 mt-1.5">
                        {cat.critical > 0 && <span className="text-[9px] bg-red-500 text-white px-1 py-0.5 rounded font-bold">{cat.critical} crit</span>}
                        {cat.high > 0     && <span className="text-[9px] bg-orange-400 text-white px-1 py-0.5 rounded font-bold">{cat.high} high</span>}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            {filterCategory && (
              <button
                onClick={() => setFilterCategory("")}
                className="mt-2 text-xs text-purple-600 hover:underline"
              >
                ✕ Clear category filter
              </button>
            )}
          </div>
        )}

        {/* ── Stats bar ──────────────────────────────────────────── */}
        {summary && (
          <div className="flex flex-wrap items-center gap-4 text-xs text-content-secondary mb-4">
            {summary.lastScannedAt && <span>Last scanned: {timeAgo(summary.lastScannedAt)}</span>}
            <span>Acknowledged: {summary.totalAcknowledged}</span>
            <span>Resolved: {summary.totalResolved}</span>
            <span>False Positive: {summary.totalFalsePositive}</span>
          </div>
        )}

        {/* ── Filters ────────────────────────────────────────────── */}
        {config && (
          <div className="flex flex-wrap gap-3 mb-4">
            <select value={filterSeverity} onChange={e => setFilterSeverity(e.target.value)}
              className="px-3 py-1.5 text-sm border border-theme-border rounded-md bg-theme-input text-content-primary focus:outline-none focus:ring-1 focus:ring-purple-500">
              <option value="">All Severities</option>
              {["Critical", "High", "Medium", "Low"].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
              className="px-3 py-1.5 text-sm border border-theme-border rounded-md bg-theme-input text-content-primary focus:outline-none focus:ring-1 focus:ring-purple-500">
              <option value="">All Statuses</option>
              {STATUS_OPTIONS.map(s => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
            </select>
            <select value={filterType} onChange={e => setFilterType(e.target.value)}
              className="px-3 py-1.5 text-sm border border-theme-border rounded-md bg-theme-input text-content-primary focus:outline-none focus:ring-1 focus:ring-purple-500">
              <option value="">All Finding Types</option>
              {findingTypes.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <select value={filterSource} onChange={e => setFilterSource(e.target.value)}
              className="hidden px-3 py-1.5 text-sm border border-theme-border rounded-md bg-theme-input text-content-primary focus:outline-none focus:ring-1 focus:ring-purple-500">
              <option value="">All Sources</option>
              <option value="Shodan">Shodan</option>
              <option value="Netlas">Netlas</option>
              <option value="Other">Other Scanners</option>
            </select>
          </div>
        )}

        {/* ── Empty state ────────────────────────────────────────── */}
        {config && displayedFindings.length === 0 && !loading && (
          <Card className="text-center py-10">
            <CheckCircle className="h-10 w-10 text-green-400 mx-auto mb-2" />
            <p className="text-content-secondary text-sm">
              {filterSeverity || filterStatus || filterType || filterSource || filterCategory
                ? "No findings match the selected filters."
                : "No findings detected. Run a scan to check your attack surface."}
            </p>
          </Card>
        )}

        {/* ── Analyst: Action Queue ──────────────────────────────── */}
        {displayedFindings.length > 0 && (
          <div className="space-y-3">
            {displayedFindings.map(f => (
              <FindingCard
                key={f.id}
                finding={f}
                risk={risksByFindingId[f.id] || null}
                isAdmin={isAdmin}
                onStatusChange={handleStatusChange}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Source badge helper ──────────────────────────────────────────────────────

function getSource(evidence) {
  if (!evidence) return null;
  if (evidence.includes("Found via Shodan")) return "Shodan";
  if (evidence.includes("Found via Netlas")) return "Netlas";
  return null;
}

const SOURCE_STYLE = {
  Shodan: "bg-blue-50 text-blue-700 border-blue-200",
  Netlas: "bg-orange-50 text-orange-700 border-orange-200",
};

// ─── Analyst: Finding Card ────────────────────────────────────────────────────

function FindingCard({ finding, risk, isAdmin, onStatusChange }) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [confirmingFp, setConfirmingFp] = useState(false);
  const [fpNote, setFpNote] = useState("");
  const severity   = SEVERITY_COLORS[finding.severity] || SEVERITY_COLORS.High;
  const asset      = finding.subdomain || finding.domain;
  const source     = getSource(finding.evidence);
  const hasRisk    = risk && risk.status !== "deleted";
  const isRiskClosed = hasRisk && risk.status === "closed";
  const ratingStyle = hasRisk ? (RISK_RATING_STYLE[risk.inherentRiskRating?.toUpperCase()] || RISK_RATING_STYLE.MEDIUM) : null;

  const ageLabel    = timeAgo(finding.firstDetectedAt);
  const stillToday  = isToday(finding.lastSeenAt);
  const catMeta     = hasRisk ? CATEGORY_META[risk.category] : null;
  const fixMinutes  = catMeta?.fixMin ?? null;

  return (
    <Card className="p-0 overflow-hidden">
      {/* ── Collapsed header ─────────────────────────────────────── */}
      <div className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-theme-hover" onClick={() => setExpanded(p => !p)}>
        <div className="flex-shrink-0">
          {expanded ? <ChevronDown className="h-4 w-4 text-content-secondary" /> : <ChevronRight className="h-4 w-4 text-content-secondary" />}
        </div>

        <span className={`flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${severity.badge}`}>
          {finding.severity}
        </span>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm text-content-primary">{finding.title}</span>
            <span className="text-xs text-content-secondary bg-theme-surface px-1.5 py-0.5 rounded border border-theme-border">
              {finding.findingType}
            </span>
            {source && (
              <span className={`text-xs px-1.5 py-0.5 rounded border font-medium ${SOURCE_STYLE[source]}`}>
                {source}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            <span className="text-xs font-mono text-content-secondary">{asset}</span>
            <span className="text-[10px] text-content-secondary">·</span>
            <span className="text-[10px] text-content-secondary">
              Found {ageLabel}
              {stillToday ? <span className="ml-1 text-orange-500 font-medium">· still present today</span>
                          : <span className="ml-1 text-content-secondary">· last seen {timeAgo(finding.lastSeenAt)}</span>}
            </span>
          </div>
        </div>

        {/* Business risk pill */}
        {hasRisk ? (
          <div className="flex-shrink-0 flex items-center gap-1.5">
            <Zap className="h-3.5 w-3.5 text-purple-500" />
            {isRiskClosed ? (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                Closed
              </span>
            ) : (
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${ratingStyle.pill}`}>
                {risk.inherentRiskRating} Risk
              </span>
            )}
          </div>
        ) : (
          <span className="flex-shrink-0 text-xs text-content-secondary italic">No risk yet</span>
        )}

        {/* Fix owner (collapsed) */}
        {hasRisk && risk.fixOwner && (
          <span className="flex-shrink-0 hidden lg:flex items-center gap-1 text-xs text-content-secondary">
            <User className="h-3 w-3" /> {risk.fixOwner}
          </span>
        )}

        {/* Fix time */}
        {fixMinutes && (
          <span className="flex-shrink-0 hidden md:flex items-center gap-1 text-xs text-content-secondary">
            <Clock className="h-3 w-3" /> ~{fixMinutes}m
          </span>
        )}

        {/* Current status badge */}
        <span className="flex-shrink-0 text-xs text-content-secondary px-2 py-0.5 rounded border border-theme-border bg-theme-surface">
          {STATUS_LABEL[finding.status] || finding.status}
        </span>
      </div>

      {/* ── Expanded panel ──────────────────────────────────────── */}
      {expanded && (
        <div className="border-t border-theme-border">
          <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-theme-border">

            {/* Left: Technical finding */}
            <div className="px-5 py-4 space-y-3 bg-theme-surface">
              <p className="text-xs font-semibold text-content-secondary uppercase tracking-wider">Technical Finding</p>

              <div className="space-y-1 text-xs text-content-secondary">
                <div><span className="font-medium text-content-primary">Asset: </span>{asset}</div>
                <div>
                  <span className="font-medium text-content-primary">Found: </span>{ageLabel}
                  {stillToday
                    ? <span className="ml-1 font-medium text-orange-500">· Still present today</span>
                    : <span className="ml-1">· Last seen {timeAgo(finding.lastSeenAt)}</span>}
                </div>
              </div>

              {finding.description && (
                <p className="text-xs text-content-secondary">{finding.description}</p>
              )}

              {finding.evidence && (
                <div className="flex gap-2">
                  <Info className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="text-xs font-medium text-content-primary">Evidence: </span>
                    <span className="text-xs text-content-secondary">{finding.evidence}</span>
                  </div>
                </div>
              )}

              {finding.remediationGuidance && (
                <div className="flex gap-2">
                  <AlertTriangle className="h-4 w-4 text-orange-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="text-xs font-medium text-content-primary">Technical Guidance: </span>
                    <span className="text-xs text-content-secondary">{finding.remediationGuidance}</span>
                  </div>
                </div>
              )}

              {/* Action buttons */}
              {isAdmin && (
                <div className="pt-2 border-t border-theme-border space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    {finding.status !== "Resolved" && (
                      <button
                        title={`${GUIDE_BY_STATUS.Resolved.when} ${GUIDE_BY_STATUS.Resolved.effect}`}
                        onClick={e => { e.stopPropagation(); onStatusChange(finding.id, "Resolved"); }}
                        className="px-3 py-1 text-xs rounded border border-green-300 text-green-700 hover:bg-green-50 font-medium"
                      >
                        ✓ Mark Resolved
                      </button>
                    )}
                    {finding.status !== "Acknowledged" && (
                      <button
                        title={`${GUIDE_BY_STATUS.Acknowledged.when} ${GUIDE_BY_STATUS.Acknowledged.effect}`}
                        onClick={e => { e.stopPropagation(); onStatusChange(finding.id, "Acknowledged"); }}
                        className="px-3 py-1 text-xs rounded border border-blue-300 text-blue-700 hover:bg-blue-50 font-medium"
                      >
                        Acknowledge
                      </button>
                    )}
                    {finding.status !== "FalsePositive" && (
                      <button
                        title={`${GUIDE_BY_STATUS.FalsePositive.when} ${GUIDE_BY_STATUS.FalsePositive.effect}`}
                        onClick={e => { e.stopPropagation(); setConfirmingFp(true); }}
                        className="px-3 py-1 text-xs rounded border border-theme-border text-content-secondary hover:bg-theme-hover"
                      >
                        False Positive
                      </button>
                    )}
                    {finding.status !== "Open" && (
                      <button
                        title={`${GUIDE_BY_STATUS.Open.when} ${GUIDE_BY_STATUS.Open.effect}`}
                        onClick={e => { e.stopPropagation(); onStatusChange(finding.id, "Open"); }}
                        className="px-3 py-1 text-xs rounded border border-theme-border text-content-secondary hover:bg-theme-hover"
                      >
                        Reopen
                      </button>
                    )}
                    <button
                      type="button"
                      aria-expanded={showGuide}
                      onClick={e => { e.stopPropagation(); setShowGuide(p => !p); }}
                      className="ml-auto text-xs text-purple-600 hover:underline flex items-center gap-1"
                    >
                      <Info className="h-3 w-3" /> Which should I choose?
                    </button>
                  </div>

                  {showGuide && (
                    <div data-testid="esm-status-guide" className="rounded border border-theme-border bg-theme-bg p-3 space-y-2">
                      {STATUS_GUIDE.map(g => (
                        <div key={g.status} className="text-xs">
                          <span className="font-semibold text-content-primary">{g.label}: </span>
                          <span className="text-content-primary">{g.when} </span>
                          <span className="text-content-secondary">{g.effect}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* False Positive is the one choice a later scan never undoes, so it asks first. */}
                  {confirmingFp && (
                    <div data-testid="esm-fp-confirm" className="rounded border border-amber-200 bg-amber-50 p-3 space-y-2" onClick={e => e.stopPropagation()}>
                      <p className="text-xs text-amber-800">
                        Mark as False Positive? Later scans will keep it closed even if they find it again.
                      </p>
                      <textarea
                        value={fpNote}
                        onChange={e => setFpNote(e.target.value)}
                        maxLength={500}
                        rows={2}
                        placeholder="Reason (optional), e.g. parked domain that never sends email"
                        aria-label="Reason for marking as false positive"
                        className="w-full px-2 py-1 text-xs border border-theme-border rounded bg-theme-input text-content-primary focus:outline-none focus:ring-1 focus:ring-purple-500"
                      />
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => { onStatusChange(finding.id, "FalsePositive", fpNote.trim()); setConfirmingFp(false); setFpNote(""); }}
                          className="px-3 py-1 text-xs rounded bg-amber-600 text-white hover:bg-amber-700 font-medium"
                        >
                          Confirm False Positive
                        </button>
                        <button
                          type="button"
                          onClick={() => { setConfirmingFp(false); setFpNote(""); }}
                          className="px-3 py-1 text-xs rounded border border-theme-border text-content-secondary hover:bg-theme-hover"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <FindingActivity findingId={finding.id} status={finding.status} />
            </div>

            {/* Right: Business risk */}
            {hasRisk ? (
              <div className={`border-l-4 ${ratingStyle.accent} bg-theme-surface`}>
                <div className="px-5 pt-4 pb-3 border-b border-theme-border">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold text-content-secondary uppercase tracking-wider flex items-center gap-1.5">
                      <TrendingUp className="h-3.5 w-3.5 text-purple-500" /> Business Risk
                    </span>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded border ${ratingStyle.pill}`}>{risk.inherentRiskRating}</span>
                      <span className="text-xs text-content-secondary">{RISK_STATUS_LABEL[risk.status] || risk.status}</span>
                    </div>
                  </div>
                  <p className="text-sm font-semibold text-content-primary leading-snug">{risk.title}</p>
                </div>

                <div className="px-5 py-3 space-y-3">
                  {/* Monetary impact — financial engine values when available, text fallback otherwise */}
                  {risk.finalCalculatedMonetaryImpact != null ? (
                    <div>
                      <p className="text-xs font-semibold text-content-secondary uppercase tracking-wider mb-1 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3 text-orange-500" /> Business Impact
                      </p>
                      <div className="p-2.5 bg-emerald-50 rounded-lg border border-emerald-200">
                        {risk.impactLow != null && risk.impactHigh != null ? (
                          <div className="text-sm font-bold text-emerald-700">
                            {formatImpact(risk.impactLow)} – {formatImpact(risk.impactHigh)}+
                            <span className="ml-2 text-xs font-normal text-emerald-600">
                              (Most Likely: {formatImpact(risk.finalCalculatedMonetaryImpact)})
                            </span>
                          </div>
                        ) : (
                          <div className="text-sm font-bold text-emerald-700">
                            {formatImpact(risk.finalCalculatedMonetaryImpact)}
                          </div>
                        )}
                        <p className="text-[10px] text-emerald-600 mt-0.5">Estimated annual exposure · Financial engine</p>
                      </div>
                    </div>
                  ) : risk.businessImpact && (
                    <div>
                      <p className="text-xs font-semibold text-content-secondary uppercase tracking-wider mb-1 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3 text-orange-500" /> Business Impact
                      </p>
                      <p className="text-xs text-content-primary leading-relaxed">{risk.businessImpact}</p>
                    </div>
                  )}

                  {/* The two long texts fold away so an expanded finding stays a screen, not a page. */}
                  {risk.whatMayBreak && (
                    <Collapsible icon={<AlertTriangle className="h-3 w-3 text-red-500" />} title="What May Break">
                      <p className="text-xs text-content-primary leading-relaxed whitespace-pre-line">{risk.whatMayBreak}</p>
                    </Collapsible>
                  )}

                  {risk.remediationSteps && (
                    <Collapsible icon={<Wrench className="h-3 w-3 text-blue-500" />} title="What To Do">
                      <p className="text-xs text-content-primary leading-relaxed whitespace-pre-line">{risk.remediationSteps}</p>
                    </Collapsible>
                  )}

                  <div className="flex flex-wrap items-center gap-3 pt-2 text-xs text-content-secondary border-t border-theme-border">
                    {risk.fixOwner && (
                      <span className="flex items-center gap-1 font-medium text-content-primary">
                        <User className="h-3 w-3" /> {risk.fixOwner}
                      </span>
                    )}
                    {fixMinutes && (
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" /> Estimated fix: ~{fixMinutes} min
                      </span>
                    )}
                    <span>Category: {catMeta?.label ?? risk.category}</span>
                    <span>Identified {timeAgo(risk.createdAt)}</span>
                    {risk.grcRiskId && (
                      <button
                        onClick={e => { e.stopPropagation(); navigate(`/risks?riskId=${risk.grcRiskId}`); }}
                        className="ml-auto flex items-center gap-1.5 px-2.5 py-1 border rounded-lg font-medium transition-all bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-600 hover:text-white hover:border-emerald-600 active:scale-95"
                      >
                        <ExternalLink className="h-3 w-3" />
                        View Risk
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="px-5 py-4 flex flex-col items-center justify-center text-center text-xs text-content-secondary gap-2 bg-theme-surface">
                <Shield className="h-6 w-6 text-content-secondary opacity-30" />
                <span>Business risk will appear after the next scan completes.</span>
              </div>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

// ─── Fold-out section ─────────────────────────────────────────────────────────

function Collapsible({ icon, title, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div>
      <button
        type="button"
        aria-expanded={open}
        onClick={e => { e.stopPropagation(); setOpen(p => !p); }}
        className="w-full flex items-center gap-1 text-xs font-semibold text-content-secondary uppercase tracking-wider hover:text-content-primary"
      >
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        {icon} {title}
      </button>
      {open && <div className="mt-1">{children}</div>}
    </div>
  );
}

// ─── Activity log: who changed the status, when, and why ─────────────────────

function FindingActivity({ findingId, status }) {
  const apiCall = useApi();
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState(null);
  const [error, setError] = useState(false);

  // Loaded only when opened, and again after a status change so the newest entry appears.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setError(false);
    apiCall("GET", `/esm/findings/${findingId}/activity`)
      .then(rows => { if (!cancelled) setEntries(Array.isArray(rows) ? rows : []); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [open, findingId, status]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="pt-2 border-t border-theme-border" data-testid="esm-activity">
      <button
        type="button"
        aria-expanded={open}
        onClick={e => { e.stopPropagation(); setOpen(p => !p); }}
        className="w-full flex items-center gap-1 text-xs font-semibold text-content-secondary uppercase tracking-wider hover:text-content-primary"
      >
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        <Clock className="h-3 w-3" /> Activity
      </button>
      {open && (
        <div className="mt-2 space-y-2">
          {error ? (
            <p className="text-xs text-red-600">Could not load the activity for this finding.</p>
          ) : entries === null ? (
            <p className="text-xs text-content-secondary flex items-center gap-1"><Loader className="h-3 w-3 animate-spin" /> Loading…</p>
          ) : entries.length === 0 ? (
            <p className="text-xs text-content-secondary">No status changes recorded yet.</p>
          ) : (
            <ul className="space-y-2">
              {entries.map(e => (
                <li key={e.id} className="text-xs">
                  <div className="text-content-primary">
                    <span className="font-medium">{e.isSystem ? "System (scan)" : e.changedBy}</span>
                    {" changed "}
                    {STATUS_LABEL[e.previousStatus] || e.previousStatus || "—"} → <span className="font-medium">{STATUS_LABEL[e.newStatus] || e.newStatus}</span>
                  </div>
                  <div className="text-content-secondary" title={new Date(e.changedAt).toLocaleString()}>
                    {new Date(e.changedAt).toLocaleString()} · {timeAgo(e.changedAt)}
                  </div>
                  {e.note && <div className="text-content-secondary italic">“{e.note}”</div>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// ─── CIO: Attack Surface Score Card ──────────────────────────────────────────

function AttackSurfaceScoreCard({ summary, domainCount, subdomainCount }) {
  const score = summary.attackSurfaceScore ?? 100;
  const level = summary.attackSurfaceRiskLevel ?? "UNKNOWN";
  const cfg = {
    CRITICAL: { bar: "bg-red-500",    text: "text-red-600",    bg: "bg-red-50",    border: "border-red-200",    label: "CRITICAL RISK"  },
    HIGH:     { bar: "bg-orange-500", text: "text-orange-600", bg: "bg-orange-50", border: "border-orange-200", label: "HIGH RISK"      },
    MEDIUM:   { bar: "bg-yellow-500", text: "text-yellow-600", bg: "bg-yellow-50", border: "border-yellow-200", label: "MEDIUM RISK"    },
    LOW:      { bar: "bg-green-500",  text: "text-green-600",  bg: "bg-green-50",  border: "border-green-200",  label: "LOW RISK"       },
    UNKNOWN:  { bar: "bg-gray-400",   text: "text-gray-500",   bg: "bg-gray-50",   border: "border-gray-200",   label: "NOT SCANNED"    },
  }[level] ?? { bar: "bg-gray-400", text: "text-gray-500", bg: "bg-gray-50", border: "border-gray-200", label: level };

  return (
    <div className={`rounded-lg border ${cfg.border} ${cfg.bg} p-5 mb-6`}>
      <div className="flex items-start gap-6 flex-wrap">
        {/* Score */}
        <div className="flex-shrink-0 text-center">
          <div className={`text-5xl font-black ${cfg.text}`}>{score}</div>
          <div className="text-xs text-content-secondary font-medium mt-0.5">/ 100</div>
        </div>

        {/* Bar + meta */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-sm font-semibold text-content-primary">Attack Surface Score</span>
            <span className={`text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded ${cfg.text} bg-white/70 border ${cfg.border}`}>
              {cfg.label}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
            <div className={`h-2.5 rounded-full transition-all ${cfg.bar}`} style={{ width: `${score}%` }} />
          </div>
          <div className="flex justify-between text-[10px] text-content-secondary mb-3">
            <span>0 — Critical</span><span>100 — Clean</span>
          </div>

          {/* Monitored assets */}
          <div className="flex flex-wrap gap-3 text-xs text-content-secondary">
            {domainCount > 0 && <span className="flex items-center gap-1"><Globe className="h-3 w-3" /> {domainCount} domain{domainCount !== 1 ? "s" : ""} monitored</span>}
            {subdomainCount > 0 && <span className="flex items-center gap-1"><Globe className="h-3 w-3 opacity-50" /> {subdomainCount} subdomain{subdomainCount !== 1 ? "s" : ""} discovered</span>}
            {summary.lastScannedAt && <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> Last scanned {timeAgo(summary.lastScannedAt)}</span>}
          </div>
        </div>

        {/* Severity breakdown */}
        <div className="flex-shrink-0 flex flex-col gap-2 text-xs min-w-[140px]">
          {[
            { label: "Critical", count: summary.critical, pts: 15, color: "text-red-600",    bar: "bg-red-500" },
            { label: "High",     count: summary.high,     pts: 8,  color: "text-orange-500", bar: "bg-orange-500" },
            { label: "Medium",   count: summary.medium,   pts: 3,  color: "text-yellow-600", bar: "bg-yellow-400" },
            { label: "Low",      count: summary.low,      pts: 1,  color: "text-gray-500",   bar: "bg-gray-400" },
          ].map(({ label, count, pts, color, bar }) => (
            <div key={label} className="flex items-center gap-2">
              <span className={`w-14 text-right font-medium ${color}`}>{count} {label}</span>
              <div className="flex-1 bg-gray-200 rounded h-1.5">
                <div className={`h-1.5 rounded ${bar}`} style={{ width: count > 0 ? `${Math.min(100, count * pts * 2)}%` : "0%" }} />
              </div>
              {count > 0 && <span className={`text-[10px] ${color}`}>−{count * pts}pts</span>}
            </div>
          ))}
          {summary.totalOpen === 0 && <span className="text-green-600 font-semibold mt-1">✓ No open findings</span>}
        </div>
      </div>
    </div>
  );
}
